import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Client-side shopping cart for the public storefront (no server cart in v1).
 * The cart is scoped to ONE store slug — adding from a different store resets it.
 * Prices here are display-only; the server re-resolves every price at checkout.
 */
export interface CartLine {
  itemId: string;
  itemUnitId?: string;
  itemVariantId?: string;
  name: string;
  price: string;          // display only
  currencyCode: string;
  imageUrl: string | null;
  quantity: number;
}

const keyOf = (l: Pick<CartLine, 'itemId' | 'itemUnitId' | 'itemVariantId'>): string =>
  `${l.itemId}:${l.itemUnitId ?? ''}:${l.itemVariantId ?? ''}`;

interface CartState {
  slug: string | null;
  items: CartLine[];
  add(slug: string, line: CartLine): void;
  setQty(key: string, quantity: number): void;
  remove(key: string): void;
  /** Drop any lines not priced in `currencyCode` (store currency changed). */
  pruneCurrency(currencyCode: string): void;
  clear(): void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      slug: null,
      items: [],
      add: (slug, line) =>
        set((state) => {
          // Switching stores starts a fresh cart.
          const prior = state.slug === slug ? state.items : [];
          // Lines are priced in the store's active currency; if the admin changed
          // it, drop any lines still carrying the old currency so totals never mix.
          const items = prior.filter((i) => i.currencyCode === line.currencyCode);
          const k = keyOf(line);
          const existing = items.find((i) => keyOf(i) === k);
          if (existing) existing.quantity = Math.min(existing.quantity + line.quantity, 100000);
          else items.push({ ...line, quantity: Math.min(line.quantity, 100000) });
          return { slug, items };
        }),
      setQty: (key, quantity) =>
        set((state) => ({
          items: state.items
            .map((i) => (keyOf(i) === key ? { ...i, quantity: Math.max(1, Math.min(quantity, 100000)) } : i)),
        })),
      remove: (key) => set((state) => ({ items: state.items.filter((i) => keyOf(i) !== key) })),
      pruneCurrency: (currencyCode) =>
        set((state) => {
          const items = state.items.filter((i) => i.currencyCode === currencyCode);
          return items.length === state.items.length ? state : { items };
        }),
      clear: () => set({ items: [] }),
    }),
    { name: 'erp-store-cart', storage: createJSONStorage(() => localStorage) },
  ),
);

export const cartKey = keyOf;
export const cartCount = (items: CartLine[]): number => items.reduce((n, i) => n + i.quantity, 0);
